import { Container } from '@mui/material';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import { AppRouter } from './AppRouter';
import { container } from './common/ioc/ioc.config';
import { INJECTED_TYPES } from './common/ioc/ioc.types';
import { useCommonBoundStore } from './common/stores/useStore';
import {
  AbilityContext,
  AppAbility,
  ICommandInvoker,
  IErrorHandlerService,
  IUserProfileService,
  defineAbilitiesFor,
  useAuthConfig
} from './shared';
import EvergineCommand from './shared/commands/evergineCommand';
import { loadSettings } from './shared/settings';
import './styles/app.scss';
import { useBoundStore } from './surgeries/stores/useStore';

export default function App() {
  const [t] = useTranslation();
  const updateProfile = useBoundStore((state) => state.updateProfile);
  const { setIsTouchDevice, isTouchDevice, setSettings } = useCommonBoundStore();
  const { isLogged } = useAuthConfig();
  const [userIsLogged, setUserIsLogged] = useState<boolean>();
  const [ability, setAbility] = useState<AppAbility>();
  const newActionCommand = useBoundStore((state) => state.newActionCommand);
  const commandInvokerService = container.get<ICommandInvoker>(INJECTED_TYPES.ICommandInvokerService);
  const errorHandlerService = container.get<IErrorHandlerService>(INJECTED_TYPES.IErrorHandlerService);

  useEffect(() => {
    async function initSettings() {
      const settings = await loadSettings();
      setSettings(settings);
      if (settings.testE2E === true) {
        window.testE2E = true;
      }
    }
    initSettings();
  }, []);

  useEffect(() => {
    setUserIsLogged(isLogged);

    if (isLogged === false) {
      errorHandlerService.showError(t('errors.authError'));
    }
  }, [isLogged]);

  useEffect(() => {
    if (isTouchDevice !== null) {
      return;
    }

    const checkIsTouchDevice = () => {
      return 'ontouchstart' in (window || document.documentElement) || navigator.maxTouchPoints > 0;
    };

    setIsTouchDevice(checkIsTouchDevice());
  }, []);

  useEffect(() => {
    const getUser = async () => {
      if (isLogged) {
        const profileService = container.get<IUserProfileService>(INJECTED_TYPES.IUserProfileService);
        const userProfile = await profileService.getProfile();

        updateProfile(userProfile);
        setAbility(defineAbilitiesFor(userProfile.type));
      }
    };

    getUser();
  }, [isLogged]);

  useEffect(() => {
    if (!newActionCommand) {
      return;
    }
    const command = new EvergineCommand(newActionCommand.id);
    commandInvokerService.addEvergineCommand(command);
  }, [newActionCommand]);

  return (
    <>
      {userIsLogged && ability && (
        <AbilityContext.Provider value={ability}>
          <BrowserRouter>
            <Container id="app" disableGutters={true} maxWidth={false}>
              <AppRouter />
            </Container>
          </BrowserRouter>
        </AbilityContext.Provider>
      )}
    </>
  );
}
